/**
 * Scoring Utility Functions
 * Calculate scores based on user decisions and actions
 */

/**
 * Calculate scenario score based on decisions and actions
 * @param {Object} decisions - User decisions made during scenario
 * @param {Object} successCriteria - Success criteria for the scenario
 * @returns {Object} Score breakdown
 */
export const calculateScenarioScore = (decisions, successCriteria) => {
  // This is a simplified scoring algorithm
  // In production, this would be more sophisticated

  // Analyze decisions against success criteria
  // For now, return a mock score structure
  // In production, this would analyze actual decisions
  
  const score = {
    impact: Math.min(100, Math.max(0, 70 + Math.random() * 30)),
    feasibility: Math.min(100, Math.max(0, 70 + Math.random() * 30)),
    stakeholderManagement: Math.min(100, Math.max(0, 70 + Math.random() * 30)),
    speed: Math.min(100, Math.max(0, 70 + Math.random() * 30)),
    frameworkApplication: Math.min(100, Math.max(0, 70 + Math.random() * 30)),
  };

  // Calculate weighted total
  score.total = (
    score.impact * 0.3 +
    score.feasibility * 0.2 +
    score.stakeholderManagement * 0.25 +
    score.speed * 0.1 +
    score.frameworkApplication * 0.15
  );

  // Calculate skill improvements
  score.skills = {
    dataLiteracy: score.impact * 0.3 + score.frameworkApplication * 0.2,
    stakeholderManagement: score.stakeholderManagement,
    technicalDepth: score.feasibility * 0.5 + score.frameworkApplication * 0.3,
    businessAcumen: score.impact * 0.5 + score.frameworkApplication * 0.2
  };

  return score;
};

/**
 * Get score feedback message - concise and actionable
 */
export const getScoreFeedback = (score) => {
  const feedback = [];
  
  // Impact feedback - specific and actionable
  if (score.impact >= 80) {
    feedback.push({
      type: 'strength',
      category: 'Impact',
      message: 'Your prioritization was strong because you clearly connected decisions to business outcomes.',
      score: score.impact
    });
  } else if (score.impact >= 60) {
    feedback.push({
      type: 'improvement',
      category: 'Impact',
      message: 'Consider quantifying business impact more explicitly. Ask: "How does this move the needle?"',
      score: score.impact
    });
  } else {
    feedback.push({
      type: 'improvement',
      category: 'Impact',
      message: 'You missed connecting decisions to business value. Use frameworks like RICE to quantify impact.',
      score: score.impact
    });
  }

  // Stakeholder Management feedback
  if (score.stakeholderManagement >= 80) {
    feedback.push({
      type: 'strength',
      category: 'Stakeholder Management',
      message: 'Your stakeholder alignment was excellent. You communicated clearly and managed expectations well.',
      score: score.stakeholderManagement
    });
  } else if (score.stakeholderManagement >= 60) {
    feedback.push({
      type: 'improvement',
      category: 'Stakeholder Management',
      message: 'Increase update frequency. Stakeholders need regular visibility to stay aligned.',
      score: score.stakeholderManagement
    });
  } else {
    feedback.push({
      type: 'improvement',
      category: 'Stakeholder Management',
      message: 'You missed a dependency - ensure all stakeholders are consulted before major decisions.',
      score: score.stakeholderManagement
    });
  }

  // Framework Application feedback
  if (score.frameworkApplication >= 80) {
    feedback.push({
      type: 'strength',
      category: 'Framework Application',
      message: 'Great use of PM frameworks! You applied structured thinking effectively.',
      score: score.frameworkApplication
    });
  } else if (score.frameworkApplication >= 60) {
    feedback.push({
      type: 'improvement',
      category: 'Framework Application',
      message: 'Consider using frameworks more consistently. RICE for prioritization, Jobs-to-be-Done for user needs.',
      score: score.frameworkApplication
    });
  } else {
    feedback.push({
      type: 'improvement',
      category: 'Framework Application',
      message: 'Apply structured frameworks. Start with RICE for prioritization decisions.',
      score: score.frameworkApplication
    });
  }

  // Segmentation feedback (if available)
  if (score.segmentation !== undefined) {
    if (score.segmentation < 60) {
      feedback.push({
        type: 'improvement',
        category: 'Segmentation',
        message: 'Your segmentation lacked specificity. Define clear user personas with distinct needs.',
        score: score.segmentation
      });
    } else if (score.segmentation >= 80) {
      feedback.push({
        type: 'strength',
        category: 'Segmentation',
        message: 'Strong user segmentation. You clearly identified and prioritized target segments.',
        score: score.segmentation
      });
    }
  }

  // Feasibility feedback
  if (score.feasibility < 60) {
    feedback.push({
      type: 'improvement',
      category: 'Feasibility',
      message: 'Consider technical constraints earlier. Work with engineering to validate assumptions.',
      score: score.feasibility
    });
  }

  return feedback;
};





